import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CERT_TYPES = [
  'SSL/TLS', 'Code Signing', 'Client Certificate', 'Server Certificate',
  'Wildcard', 'Multi-Domain', 'Self-Signed', 'Root CA'
];

const PROVIDERS = [
  "Let's Encrypt", 'DigiCert', 'GlobalSign', 'Sectigo (Comodo)',
  'GoDaddy', 'AWS Certificate Manager', 'Google Trust Services',
  'Cloudflare', 'ZeroSSL', 'Other'
];

const FORMATS = [
  { value: 'PEM', label: '.PEM (Base64 encoded)' },
  { value: 'DER', label: '.DER (Binary)' },
  { value: 'P7B', label: '.P7B / .P7C (PKCS#7)' },
  { value: 'P12', label: '.P12 (PKCS#12)' },
  { value: 'PFX', label: '.PFX (Personal Exchange)' },
  { value: 'CRT', label: '.CRT (Certificate)' },
  { value: 'CER', label: '.CER (Certificate)' },
  { value: 'KEY', label: '.KEY (Private Key)' },
];

export default function AddCertificate({ user }) {
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    certificate_type: '',
    certificate_provider: '',
    certificate_format: '',
    expiry_date: '',
    domain_names: '',
    certificate_details: '',
    notes: '',
    project_id: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setProjects(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.certificate_type || !formData.certificate_provider || !formData.certificate_format || !formData.expiry_date) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/certificates`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Certificate added successfully!');
      navigate('/dashboard/certificates');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add certificate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <button
        onClick={() => navigate('/dashboard/certificates')}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6 font-medium"
        data-testid="btn-back-to-certificates"
      >
        <ArrowLeft size={20} />
        Back to Certificates
      </button>

      <div className="max-w-2xl">
        <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight mb-2">
          Add Certificate
        </h1>
        <p className="text-text-secondary mb-8">
          Track and manage your SSL/TLS and other certificates
        </p>

        <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                placeholder="e.g., Production SSL Certificate"
                data-testid="input-cert-title"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Certificate Type *</label>
                <select
                  required
                  value={formData.certificate_type}
                  onChange={(e) => setFormData({ ...formData, certificate_type: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="select-cert-type"
                >
                  <option value="">Select Type</option>
                  {CERT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Provider *</label>
                <select
                  required
                  value={formData.certificate_provider}
                  onChange={(e) => setFormData({ ...formData, certificate_provider: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="select-cert-provider"
                >
                  <option value="">Select Provider</option>
                  {PROVIDERS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Format *</label>
                <select
                  required
                  value={formData.certificate_format}
                  onChange={(e) => setFormData({ ...formData, certificate_format: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="select-cert-format"
                >
                  <option value="">Select Format</option>
                  {FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Expiry Date *</label>
                <input
                  type="date"
                  required
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="input-cert-expiry"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Domain Names (comma-separated)
              </label>
              <input
                type="text"
                value={formData.domain_names}
                onChange={(e) => setFormData({ ...formData, domain_names: e.target.value })}
                className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                placeholder="example.com, *.example.com"
                data-testid="input-cert-domains"
              />
              <p className="text-xs text-text-muted mt-1">Enter domains covered by this certificate</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Project (Optional)</label>
              <select
                value={formData.project_id}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                data-testid="select-cert-project"
              >
                <option value="">No Project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Certificate Details</label>
              <textarea
                value={formData.certificate_details}
                onChange={(e) => setFormData({ ...formData, certificate_details: e.target.value })}
                rows={3}
                className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary resize-none"
                placeholder="Serial number, issuer details, key algorithm..."
                data-testid="input-cert-details"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary resize-none"
                placeholder="Additional notes..."
                data-testid="input-cert-notes"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1"
                data-testid="btn-save-certificate"
              >
                {loading ? 'Saving...' : 'Save Certificate'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard/certificates')}
                className="btn-outline flex-1"
                data-testid="btn-cancel-add-certificate"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
